import { useState } from 'react';
import { Listing } from '@/types/listing';
import ListingGalleryModal from './ListingGalleryModal';

interface Props {
  listing: Listing;
}

export default function ListingCard({ listing }: Props) {
  const [showGallery, setShowGallery] = useState(false);
  const photoCount = listing.images?.length ?? 0;

  return (
    <>
      <article
        onClick={() => setShowGallery(true)}
        className="bg-white rounded-lg overflow-hidden border border-gray-100 hover:border-amber-200 transition-all duration-300 group cursor-pointer"
      >
        {/* Image */}
        <div className="relative w-full h-56 overflow-hidden">
          <img
            src={listing.images?.[0] ?? ''}
            alt={listing.address}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
          {/* Photo count badge */}
          {photoCount > 1 && (
            <div className="absolute bottom-3 right-3">
              <span className="flex items-center gap-1 px-2.5 py-1 bg-black/60 text-white text-xs rounded-full backdrop-blur-sm">
                <i className="ri-image-line text-xs"></i>
                {photoCount}
              </span>
            </div>
          )}
          <div className="absolute top-3 left-3">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
              listing.status === 'New'
                ? 'bg-amber-500 text-white'
                : 'bg-white/90 text-gray-800'
            }`}>
              {listing.status}
            </span>
          </div>
          <div className="absolute top-3 right-3">
            <span className="px-3 py-1 bg-white/90 rounded-full text-xs font-medium text-gray-700 whitespace-nowrap">
              {listing.type}
            </span>
          </div>

          {/* View photos overlay on hover */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 text-gray-800 text-xs font-semibold px-4 py-2 rounded-full flex items-center gap-1.5">
              <i className="ri-image-2-line"></i>
              View Photos
            </span>
          </div>
        </div>

        {/* Info */}
        <div className="p-5">
          <p className="text-2xl font-bold text-gray-900 mb-1">{listing.price}</p>
          <p className="text-gray-600 text-sm mb-4 flex items-start gap-1">
            <span className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
              <i className="ri-map-pin-2-line text-amber-500 text-xs"></i>
            </span>
            {listing.address}
          </p>
          <p className="text-gray-500 text-xs leading-relaxed mb-4 line-clamp-2">{listing.description}</p>
          <div className="flex items-center gap-5 text-gray-500 text-xs border-t border-gray-100 pt-4">
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-hotel-bed-line text-amber-500"></i>
              </span>
              {listing.beds} Beds
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-drop-line text-amber-500"></i>
              </span>
              {listing.baths} Baths
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-ruler-line text-amber-500"></i>
              </span>
              {listing.sqft} sq ft
            </span>
          </div>
        </div>
      </article>

      {showGallery && (
        <ListingGalleryModal
          listing={listing}
          onClose={() => setShowGallery(false)}
        />
      )}
    </>
  );
}
